import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const CATALOG_TAKE = 1000;
const MAESTRO_TAKE = 500;
const MONEY_FORMAT = new Intl.NumberFormat('es-AR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const DATE_FORMAT = new Intl.DateTimeFormat('es-AR', {
  dateStyle: 'medium',
});

const emptyMaestro = () => ({
  id: 0,
  clienteId: '',
  localidadId: '',
  localidadOrigen: '',
  nombre: '',
  descripcion: '',
  zona: '',
  subIndice: '',
  prioridad: 10,
  activo: true,
  vigenciaDesde: '',
  vigenciaHasta: '',
  divisorVolumetrico: '',
  topeKgVolumetrico: '',
  tipoModelo: 'PAQUETE',
  orden: '',
});

function toNullableNumber(value) {
  if (value === '' || value === null || value === undefined) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toInputDate(value) {
  if (!value) {
    return '';
  }

  return String(value).slice(0, 10);
}

function toInputNumber(value) {
  return value === null || value === undefined ? '' : String(value);
}

function formatMoney(value) {
  return `$${MONEY_FORMAT.format(Number(value || 0))}`;
}

function formatDate(value) {
  if (!value) {
    return 'Sin datos';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Sin datos';
  }

  return DATE_FORMAT.format(parsed);
}

function buildClienteLabel(cliente) {
  return cliente.razonSocial || cliente.cuit || cliente.nombre || `Cliente ${cliente.id}`;
}

function buildClienteMeta(cliente) {
  const parts = [];

  if (cliente.cuit) {
    parts.push(`CUIT ${cliente.cuit}`);
  }

  if (cliente.codigoPostal) {
    parts.push(`CP ${cliente.codigoPostal}`);
  }

  if (cliente.domicilio) {
    parts.push(cliente.domicilio);
  }

  parts.push(`ID ${cliente.id}`);
  return parts.join(' | ');
}

function buildLocalidadLabel(localidad) {
  const name = localidad.nombre || `Localidad ${localidad.id}`;
  return `${name}${localidad.codigoPostal ? ` (${localidad.codigoPostal})` : ''}`;
}

function buildLocalidadMeta(localidad) {
  const parts = [];

  if (localidad.codigoPostal) {
    parts.push(`CP ${localidad.codigoPostal}`);
  }

  if (localidad.zona) {
    parts.push(`Zona ${localidad.zona}`);
  }

  if (localidad.subIndice) {
    parts.push(`Sub ${localidad.subIndice}`);
  }

  if (localidad.leadTime !== null && localidad.leadTime !== undefined) {
    parts.push(`Lead ${localidad.leadTime}`);
  }

  parts.push(`ID ${localidad.id}`);
  return parts.join(' | ');
}

function buildScopeSummary(maestro) {
  const parts = [];

  parts.push(maestro.clienteNombre || 'General');
  parts.push(maestro.localidadNombre || 'Sin destino');

  if (maestro.localidadOrigenNombre) {
    parts.push(`Origen ${maestro.localidadOrigenNombre}`);
  } else {
    parts.push('Sin origen');
  }

  if (maestro.zona) {
    parts.push(`Zona ${maestro.zona}`);
  }

  if (maestro.subIndice) {
    parts.push(`Sub ${maestro.subIndice}`);
  }

  return parts.join(' | ');
}

function buildRuleConditionSummary(rule) {
  const parts = [];
  const pesoMin = rule.pesoMinKg ?? '-';
  const pesoMax = rule.pesoMaxKg ?? '-';
  const bultosMin = rule.bultosMin ?? '-';
  const bultosMax = rule.bultosMax ?? '-';

  parts.push(`Peso ${pesoMin} a ${pesoMax} kg`);
  parts.push(`Bultos ${bultosMin} a ${bultosMax}`);

  const dimensions = [rule.altoCmMax, rule.anchoCmMax, rule.largoCmMax]
    .map((value) => (value === null || value === undefined ? '-' : Number(value).toFixed(0)))
    .join(' x ');
  parts.push(`Dim ${dimensions} cm`);

  if (rule.bultosExcedenteDesde !== null && rule.bultosExcedenteDesde !== undefined) {
    parts.push(`Excede desde ${rule.bultosExcedenteDesde}`);
  }

  if (rule.tipoCalculoExcedente) {
    parts.push(`Excedente ${rule.tipoCalculoExcedente}`);
  }

  return parts.join(' | ');
}

function buildRulePricingSummary(rule) {
  const parts = [`Base ${formatMoney(rule.precioBase)}`];

  if (rule.precioExcedentePorUnidad !== null && rule.precioExcedentePorUnidad !== undefined) {
    parts.push(`Exc/u ${formatMoney(rule.precioExcedentePorUnidad)}`);
  }

  if (rule.minimoFacturable !== null && rule.minimoFacturable !== undefined) {
    parts.push(`Min ${formatMoney(rule.minimoFacturable)}`);
  }

  if (rule.precioBaseRetiro !== null && rule.precioBaseRetiro !== undefined) {
    parts.push(`Base retiro ${formatMoney(rule.precioBaseRetiro)}`);
  }

  if (rule.precioExcedenteRetiro !== null && rule.precioExcedenteRetiro !== undefined) {
    parts.push(`Exc retiro ${formatMoney(rule.precioExcedenteRetiro)}`);
  }

  if (rule.pesoMaxPorBultoKg !== null && rule.pesoMaxPorBultoKg !== undefined) {
    parts.push(`Max ${Number(rule.pesoMaxPorBultoKg).toFixed(0)} kg/bulto`);
  }

  if (rule.precioExcedentePorBultoKg !== null && rule.precioExcedentePorBultoKg !== undefined) {
    parts.push(`Exc bulto ${formatMoney(rule.precioExcedentePorBultoKg)}`);
  }

  if (rule.precioTope !== null && rule.precioTope !== undefined) {
    parts.push(`Tope ${formatMoney(rule.precioTope)}`);
  }

  if (rule.precioTopeRetiro !== null && rule.precioTopeRetiro !== undefined) {
    parts.push(`Tope retiro ${formatMoney(rule.precioTopeRetiro)}`);
  }

  return parts.join(' | ');
}

function CatalogPicker({
  label,
  value,
  items,
  onChange,
  getLabel,
  getMeta,
  placeholder,
  helperText,
  emptyLabel,
  loading = false,
  allowClear = true,
  compact = false,
  maxResults = 8,
  showAllOnEmpty = false,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const selectedItem = useMemo(
    () => items.find((entry) => String(entry.id) === String(value)) || null,
    [items, value]
  );

  const selectedLabel = selectedItem ? getLabel(selectedItem) : value ? String(value) : '';
  const selectedMeta = selectedItem && getMeta ? getMeta(selectedItem) : '';

  useEffect(() => {
    setQuery(selectedLabel);
  }, [selectedLabel]);

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();
    const filtered = term
      ? items.filter((item) => {
          const haystack = [getLabel(item), getMeta ? getMeta(item) : '', String(item.id)]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return haystack.includes(term);
        })
      : showAllOnEmpty
        ? items
        : [];

    return filtered.slice(0, maxResults);
  }, [getLabel, getMeta, items, maxResults, query, showAllOnEmpty]);

  const chooseItem = (item) => {
    onChange(String(item.id));
    setQuery(getLabel(item));
    setOpen(false);
  };

  const clearSelection = () => {
    onChange('');
    setQuery('');
    setOpen(false);
  };

  const selectionHint = selectedItem
    ? `${selectedLabel}${selectedMeta ? ` | ${selectedMeta}` : ''}`
    : value
      ? `ID ${value}`
      : emptyLabel;

  return (
    <div className={`catalog-picker ${compact ? 'catalog-picker--compact' : ''}`}>
      <label>
        {label}
        <input
          type="search"
          value={query}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onBlur={() => {
            window.setTimeout(() => {
              setOpen(false);
              setQuery(selectedItem ? selectedLabel : value ? String(value) : '');
            }, 120);
          }}
          disabled={loading}
          autoComplete="off"
        />
      </label>

      <div className="catalog-picker-meta">
        <span className="catalog-picker-hint">{selectionHint}</span>
        {helperText ? <span className="catalog-picker-note">{helperText}</span> : null}
        {allowClear && value ? (
          <button type="button" className="btn-ghost btn-inline" onClick={clearSelection}>
            Limpiar
          </button>
        ) : null}
      </div>

      {loading ? (
        <div className="catalog-picker-empty">Cargando listado real...</div>
      ) : open ? (
        <div className="catalog-picker-results">
          {matches.length > 0 ? (
            matches.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`catalog-picker-option ${String(value) === String(item.id) ? 'is-selected' : ''}`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => chooseItem(item)}
              >
                <strong>{getLabel(item)}</strong>
                {getMeta ? <span>{getMeta(item)}</span> : null}
              </button>
            ))
          ) : (
            <div className="catalog-picker-empty">
              {query.trim()
                ? 'No hay coincidencias.'
                : showAllOnEmpty
                  ? 'Escribe para refinar la busqueda.'
                  : 'Escribe para ver coincidencias.'}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function AdminTarifas() {
  const { authRequestJson, logout } = useAuth();
  const [list, setList] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [localidades, setLocalidades] = useState([]);
  const [filters, setFilters] = useState({
    q: '',
    clienteId: '',
    localidadId: '',
    localidadOrigen: '',
    activo: '',
  });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyMaestro());
  const [loading, setLoading] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [error, setError] = useState(null);
  const [catalogError, setCatalogError] = useState(null);
  const [selectedDetailId, setSelectedDetailId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const listRequestSeq = useRef(0);
  const detailRequestSeq = useRef(0);

  const loadCatalogs = useCallback(async () => {
    setCatalogLoading(true);
    setCatalogError(null);

    try {
      const [clientesData, localidadesData] = await Promise.all([
        authRequestJson(`/admin/catalogos/clientes?take=${CATALOG_TAKE}`),
        authRequestJson(`/admin/catalogos/localidades?take=${CATALOG_TAKE}`),
      ]);

      setClientes(clientesData.data || []);
      setLocalidades(localidadesData.data || []);
    } catch (err) {
      if (err?.status === 401) {
        logout();
        setCatalogError('La sesion vencio. Volve a iniciar sesion.');
      } else {
        setCatalogError(err?.message || 'Error cargando catalogos');
      }
    } finally {
      setCatalogLoading(false);
    }
  }, [authRequestJson, logout]);

  const loadList = useCallback(
    async (queryFilters = filters) => {
      const requestId = ++listRequestSeq.current;
      setLoading(true);
      setError(null);

      try {
        const qs = new URLSearchParams();
        qs.set('take', String(MAESTRO_TAKE));

        if (queryFilters.q?.trim()) qs.set('q', queryFilters.q.trim());
        if (queryFilters.clienteId) qs.set('clienteId', queryFilters.clienteId);
        if (queryFilters.localidadId) qs.set('localidadId', queryFilters.localidadId);
        if (queryFilters.localidadOrigen) qs.set('localidadOrigen', queryFilters.localidadOrigen);
        if (queryFilters.activo !== '' && queryFilters.activo !== null && queryFilters.activo !== undefined) {
          qs.set('activo', queryFilters.activo);
        }

        const data = await authRequestJson(`/admin/tarifas-maestro?${qs.toString()}`);
        if (requestId !== listRequestSeq.current) {
          return;
        }

        setList(data.data || []);
      } catch (err) {
        if (requestId !== listRequestSeq.current) {
          return;
        }

        if (err?.status === 401) {
          logout();
          setError('La sesion vencio. Volve a iniciar sesion.');
        } else {
          setError(err?.message || 'Error de conexion');
        }
      } finally {
        if (requestId === listRequestSeq.current) {
          setLoading(false);
        }
      }
    },
    [authRequestJson, filters, logout]
  );

  const loadDetail = useCallback(
    async (id) => {
      const requestId = ++detailRequestSeq.current;
      setDetailLoading(true);
      setDetailError(null);

      try {
        const data = await authRequestJson(`/admin/tarifas-maestro/${id}`);
        if (requestId !== detailRequestSeq.current) {
          return null;
        }

        const detail = data.data || null;
        setSelectedDetail(detail);
        return detail;
      } catch (err) {
        if (requestId !== detailRequestSeq.current) {
          return null;
        }

        if (err?.status === 401) {
          logout();
          setDetailError('La sesion vencio. Volve a iniciar sesion.');
        } else {
          setDetailError(err?.message || 'Error cargando detalle');
        }

        return null;
      } finally {
        if (requestId === detailRequestSeq.current) {
          setDetailLoading(false);
        }
      }
    },
    [authRequestJson, logout]
  );

  const closeDetail = useCallback(() => {
    detailRequestSeq.current += 1;
    setSelectedDetailId(null);
    setSelectedDetail(null);
    setDetailLoading(false);
    setDetailError(null);
  }, []);

  const openDetail = useCallback(
    (maestro) => {
      if (!maestro?.id) {
        return;
      }

      if (String(selectedDetailId) === String(maestro.id)) {
        closeDetail();
        return;
      }

      setSelectedDetailId(maestro.id);
      setSelectedDetail(maestro);
      setDetailError(null);
      void loadDetail(maestro.id);
    },
    [closeDetail, loadDetail, selectedDetailId]
  );

  useEffect(() => {
    void loadCatalogs();
  }, [loadCatalogs]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void loadList(filters);
    }, 220);

    return () => window.clearTimeout(handle);
  }, [filters, loadList]);

  useEffect(() => {
    if (!selectedDetailId) {
      return;
    }

    const visible = list.some((item) => String(item.id) === String(selectedDetailId));
    if (!visible) {
      closeDetail();
    }
  }, [closeDetail, list, selectedDetailId]);

  const summary = useMemo(
    () => ({
      total: list.length,
      activas: list.filter((item) => item.activo).length,
      conCliente: list.filter((item) => item.clienteId !== null && item.clienteId !== undefined).length,
      conOrigen: list.filter((item) => item.localidadOrigen !== null && item.localidadOrigen !== undefined).length,
    }),
    [list]
  );

  const detailRecord = useMemo(() => {
    if (!selectedDetail || String(selectedDetail.id) !== String(selectedDetailId)) {
      return null;
    }

    return selectedDetail;
  }, [selectedDetail, selectedDetailId]);

  const startCreate = () => {
    setForm(emptyMaestro());
    setEditing(-1);
  };

  const startEdit = (item) => {
    setForm({
      id: item.id || 0,
      clienteId: toInputNumber(item.clienteId),
      localidadId: toInputNumber(item.localidadId),
      localidadOrigen: toInputNumber(item.localidadOrigen),
      nombre: item.nombre || '',
      descripcion: item.descripcion || '',
      zona: item.zona || '',
      subIndice: item.subIndice || '',
      prioridad: item.prioridad ?? 10,
      activo: !!item.activo,
      vigenciaDesde: toInputDate(item.vigenciaDesde),
      vigenciaHasta: toInputDate(item.vigenciaHasta),
      divisorVolumetrico: toInputNumber(item.divisorVolumetrico),
      topeKgVolumetrico: toInputNumber(item.topeKgVolumetrico),
      tipoModelo: item.tipoModelo || 'PAQUETE',
      orden: toInputNumber(item.orden),
    });
    setEditing(item.id || -1);

    if (String(selectedDetailId) !== String(item.id)) {
      setSelectedDetailId(item.id);
      setSelectedDetail(item);
      setDetailError(null);
      void loadDetail(item.id);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setError(null);

    const payload = {
      clienteId: toNullableNumber(form.clienteId),
      localidadId: toNullableNumber(form.localidadId),
      localidadOrigen: toNullableNumber(form.localidadOrigen),
      nombre: form.nombre.trim(),
      descripcion: form.descripcion.trim() || null,
      zona: form.zona.trim() || null,
      subIndice: form.subIndice.trim() || null,
      prioridad: Number(form.prioridad) || 0,
      activo: form.activo,
      vigenciaDesde: form.vigenciaDesde || null,
      vigenciaHasta: form.vigenciaHasta || null,
      divisorVolumetrico: toNullableNumber(form.divisorVolumetrico),
      topeKgVolumetrico: toNullableNumber(form.topeKgVolumetrico),
      tipoModelo: form.tipoModelo || 'PAQUETE',
      orden: toNullableNumber(form.orden),
    };

    const isUpdate = form.id > 0;
    const url = isUpdate ? `/admin/tarifas-maestro/${form.id}` : '/admin/tarifas-maestro';
    const method = isUpdate ? 'PUT' : 'POST';
    const savedId = isUpdate ? form.id : null;

    try {
      const data = await authRequestJson(url, {
        method,
        body: JSON.stringify(payload),
      });

      if (!data.ok) {
        throw new Error(data.error || 'Error al guardar');
      }

      setEditing(null);
      setForm(emptyMaestro());
      await loadList(filters);

      if (savedId && String(selectedDetailId) === String(savedId)) {
        await loadDetail(savedId);
      }
    } catch (err) {
      if (err?.status === 401) {
        logout();
        setError('La sesion vencio. Volve a iniciar sesion.');
      } else {
        setError(err?.message || 'Error al guardar');
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar esta tarifa maestra?')) {
      return;
    }

    setError(null);

    try {
      const data = await authRequestJson(`/admin/tarifas-maestro/${id}`, { method: 'DELETE' });
      if (!data.ok) {
        throw new Error(data.error || 'Error al eliminar');
      }

      if (String(selectedDetailId) === String(id)) {
        closeDetail();
      }

      await loadList(filters);
    } catch (err) {
      if (err?.status === 401) {
        logout();
        setError('La sesion vencio. Volve a iniciar sesion.');
      } else {
        setError(err?.message || 'Error al eliminar');
      }
    }
  };

  const isEditing = editing !== null;
  const visibleDetail = detailRecord || list.find((item) => String(item.id) === String(selectedDetailId)) || null;
  const detailRules = visibleDetail?.reglas || [];

  return (
    <div className="page-shell">
      <section className="panel admin-panel">
        <div className="panel-head">
          <div>
            <p className="eyebrow">Administracion de tarifas maestras</p>
            <h1>Catalogo vivo de maestros tarifarios</h1>
            <p className="panel-copy">
              Busca clientes y localidades por nombre, abre el detalle inline de cada tarifa y
              trabaja con datos reales, sin IDs a mano.
            </p>
          </div>

          <div className="panel-actions">
            <Link to="/admin" className="btn-secondary btn-inline">
              Volver al panel
            </Link>
            <button className="btn-primary btn-inline" onClick={startCreate} type="button">
              + Nuevo maestro
            </button>
          </div>
        </div>

        <div className="stats-strip">
          <article>
            <span>Total</span>
            <strong>{summary.total}</strong>
          </article>
          <article>
            <span>Activos</span>
            <strong>{summary.activas}</strong>
          </article>
          <article>
            <span>Con cliente</span>
            <strong>{summary.conCliente}</strong>
          </article>
          <article>
            <span>Con origen</span>
            <strong>{summary.conOrigen}</strong>
          </article>
        </div>

        {catalogError ? <div className="alert error">{catalogError}</div> : null}
        {error ? <div className="alert error">{error}</div> : null}

        <div className="filters">
          <input
            placeholder="Buscar por nombre, zona o subindice"
            value={filters.q}
            onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
          />
          <CatalogPicker
            label="Cliente"
            value={filters.clienteId}
            items={clientes}
            onChange={(value) => setFilters((prev) => ({ ...prev, clienteId: value }))}
            getLabel={buildClienteLabel}
            getMeta={buildClienteMeta}
            placeholder="Buscar cliente real"
            helperText="Busca por razon social, CUIT o ID"
            emptyLabel="Todos los clientes"
            loading={catalogLoading && clientes.length === 0}
            allowClear
            compact
            maxResults={6}
            showAllOnEmpty
          />
          <CatalogPicker
            label="Localidad destino"
            value={filters.localidadId}
            items={localidades}
            onChange={(value) => setFilters((prev) => ({ ...prev, localidadId: value }))}
            getLabel={buildLocalidadLabel}
            getMeta={buildLocalidadMeta}
            placeholder="Buscar localidad destino"
            helperText="Busca por nombre, CP, zona o subindice"
            emptyLabel="Todas las localidades"
            loading={catalogLoading && localidades.length === 0}
            allowClear
            compact
            maxResults={6}
            showAllOnEmpty
          />
          <CatalogPicker
            label="Localidad origen"
            value={filters.localidadOrigen}
            items={localidades}
            onChange={(value) => setFilters((prev) => ({ ...prev, localidadOrigen: value }))}
            getLabel={buildLocalidadLabel}
            getMeta={buildLocalidadMeta}
            placeholder="Buscar localidad origen"
            helperText="Es opcional"
            emptyLabel="Sin filtro de origen"
            loading={catalogLoading && localidades.length === 0}
            allowClear
            compact
            maxResults={6}
            showAllOnEmpty
          />
          <select
            value={filters.activo}
            onChange={(e) => setFilters((prev) => ({ ...prev, activo: e.target.value }))}
          >
            <option value="">Todos</option>
            <option value="true">Activos</option>
            <option value="false">Inactivos</option>
          </select>
          <button className="btn-secondary btn-inline" onClick={() => void loadList(filters)} type="button">
            Actualizar
          </button>
        </div>

        {catalogLoading ? <p className="empty-state">Cargando clientes y localidades reales...</p> : null}

        {isEditing && (
          <form onSubmit={handleSave} className="form-edit">
            <h2>{form.id ? 'Editar maestro' : 'Nuevo maestro'}</h2>
            <p className="panel-copy">
              El nombre, cliente y localidades se cargan con ayuda del catalogo real para evitar
              errores al tipear IDs.
            </p>

            <div className="grid-2">
              <label>
                Nombre
                <input
                  value={form.nombre}
                  onChange={(e) => setForm((prev) => ({ ...prev, nombre: e.target.value }))}
                  required
                />
              </label>

              <CatalogPicker
                label="Cliente"
                value={form.clienteId}
                items={clientes}
                onChange={(value) => setForm((prev) => ({ ...prev, clienteId: value }))}
                getLabel={buildClienteLabel}
                getMeta={buildClienteMeta}
                placeholder="Buscar cliente"
                helperText="Dejalo vacio para una tarifa general"
                emptyLabel="Maestro general"
                loading={catalogLoading && clientes.length === 0}
                allowClear
                maxResults={8}
                showAllOnEmpty
              />

              <CatalogPicker
                label="Localidad destino"
                value={form.localidadId}
                items={localidades}
                onChange={(value) => setForm((prev) => ({ ...prev, localidadId: value }))}
                getLabel={buildLocalidadLabel}
                getMeta={buildLocalidadMeta}
                placeholder="Buscar localidad"
                helperText="Destino real de la tarifa"
                emptyLabel="Sin localidad"
                loading={catalogLoading && localidades.length === 0}
                allowClear
                maxResults={8}
                showAllOnEmpty
              />

              <CatalogPicker
                label="Localidad origen"
                value={form.localidadOrigen}
                items={localidades}
                onChange={(value) => setForm((prev) => ({ ...prev, localidadOrigen: value }))}
                getLabel={buildLocalidadLabel}
                getMeta={buildLocalidadMeta}
                placeholder="Buscar localidad de origen"
                helperText="Opcional"
                emptyLabel="Sin origen"
                loading={catalogLoading && localidades.length === 0}
                allowClear
                maxResults={8}
                showAllOnEmpty
              />

              <label>
                Zona
                <input
                  value={form.zona}
                  onChange={(e) => setForm((prev) => ({ ...prev, zona: e.target.value }))}
                />
              </label>

              <label>
                Sub indice
                <input
                  value={form.subIndice}
                  onChange={(e) => setForm((prev) => ({ ...prev, subIndice: e.target.value }))}
                />
              </label>

              <label>
                Prioridad
                <input
                  type="number"
                  value={form.prioridad}
                  onChange={(e) => setForm((prev) => ({ ...prev, prioridad: e.target.value }))}
                />
              </label>

              <label>
                Tipo modelo
                <select
                  value={form.tipoModelo}
                  onChange={(e) => setForm((prev) => ({ ...prev, tipoModelo: e.target.value }))}
                >
                  <option value="PAQUETE">PAQUETE</option>
                  <option value="BIGGER">BIGGER</option>
                  <option value="PALLET">PALLET</option>
                </select>
              </label>

              <label>
                Orden
                <input
                  type="number"
                  value={form.orden}
                  onChange={(e) => setForm((prev) => ({ ...prev, orden: e.target.value }))}
                />
              </label>

              <label>
                Divisor volumetrico
                <input
                  type="number"
                  step="0.01"
                  value={form.divisorVolumetrico}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, divisorVolumetrico: e.target.value }))
                  }
                />
              </label>

              <label>
                Tope kg volumetrico
                <input
                  type="number"
                  step="0.01"
                  value={form.topeKgVolumetrico}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, topeKgVolumetrico: e.target.value }))
                  }
                />
              </label>

              <label>
                Vigencia desde
                <input
                  type="date"
                  value={form.vigenciaDesde}
                  onChange={(e) => setForm((prev) => ({ ...prev, vigenciaDesde: e.target.value }))}
                />
              </label>

              <label>
                Vigencia hasta
                <input
                  type="date"
                  value={form.vigenciaHasta}
                  onChange={(e) => setForm((prev) => ({ ...prev, vigenciaHasta: e.target.value }))}
                />
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.activo}
                  onChange={(e) => setForm((prev) => ({ ...prev, activo: e.target.checked }))}
                />
                Activo
              </label>
            </div>

            <label>
              Descripcion
              <textarea
                rows="3"
                value={form.descripcion}
                onChange={(e) => setForm((prev) => ({ ...prev, descripcion: e.target.value }))}
              />
            </label>

            <div className="form-actions">
              <button type="submit" className="btn-primary btn-inline">
                Guardar
              </button>
              <button type="button" className="btn-secondary btn-inline" onClick={() => setEditing(null)}>
                Cancelar
              </button>
            </div>
          </form>
        )}

        {loading && list.length === 0 ? (
          <p className="empty-state">Cargando tarifas maestras reales...</p>
        ) : (
          <div className="table-shell">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nombre</th>
                  <th>Cliente</th>
                  <th>Destino</th>
                  <th>Origen</th>
                  <th>Zona / Sub</th>
                  <th>Prioridad</th>
                  <th>Reglas</th>
                  <th>Activo</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.map((maestro) => {
                  const rowIsOpen = String(selectedDetailId) === String(maestro.id);
                  const rowDetail =
                    rowIsOpen && visibleDetail && String(visibleDetail.id) === String(maestro.id)
                      ? visibleDetail
                      : maestro;

                  return (
                    <Fragment key={maestro.id}>
                      <tr className={rowIsOpen ? 'table-row-selected' : ''}>
                        <td>{maestro.id}</td>
                        <td>
                          <strong>{maestro.nombre}</strong>
                          <div className="table-muted">
                            {maestro.tipoModelo || 'PAQUETE'}
                            {maestro.orden !== null && maestro.orden !== undefined
                              ? ` / orden ${maestro.orden}`
                              : ''}
                          </div>
                        </td>
                        <td>{maestro.clienteNombre || (maestro.clienteId ? `Cliente ${maestro.clienteId}` : 'General')}</td>
                        <td>{maestro.localidadNombre || (maestro.localidadId ? `Localidad ${maestro.localidadId}` : 'Sin localidad')}</td>
                        <td>
                          {maestro.localidadOrigenNombre ||
                            (maestro.localidadOrigen ? `Localidad ${maestro.localidadOrigen}` : 'Sin origen')}
                        </td>
                        <td>
                          <div>{maestro.zona || '-'}</div>
                          <div className="table-muted">{maestro.subIndice || '-'}</div>
                        </td>
                        <td>{maestro.prioridad}</td>
                        <td>
                          <div>{maestro.reglasActivas || 0} activas</div>
                          <div className="table-muted">{maestro.reglasTotal || 0} total</div>
                        </td>
                        <td>{maestro.activo ? 'Si' : 'No'}</td>
                        <td>
                          <div className="row-actions">
                            <button
                              className="btn-secondary btn-inline"
                              onClick={() => openDetail(maestro)}
                              type="button"
                            >
                              {rowIsOpen ? 'Ocultar detalle' : 'Detalle'}
                            </button>
                            <button
                              className="btn-secondary btn-inline"
                              onClick={() => startEdit(maestro)}
                              type="button"
                            >
                              Editar
                            </button>
                            <Link className="btn-secondary btn-inline" to={`/admin/reglas?maestroId=${maestro.id}`}>
                              Reglas
                            </Link>
                            <button
                              className="btn-secondary btn-inline danger"
                              onClick={() => handleDelete(maestro.id)}
                              type="button"
                            >
                              Eliminar
                            </button>
                          </div>
                        </td>
                      </tr>

                      {rowIsOpen ? (
                        <tr className="tariff-detail-row">
                          <td colSpan="10">
                            <div className="tariff-detail-panel">
                              <div className="panel-head">
                                <div>
                                  <p className="eyebrow">Detalle inline</p>
                                  <h3>{rowDetail.nombre}</h3>
                                  <p className="panel-copy">{buildScopeSummary(rowDetail)}</p>
                                </div>
                                <div className="panel-actions">
                                  <Link className="btn-secondary btn-inline" to={`/admin/reglas?maestroId=${rowDetail.id}`}>
                                    Ir a reglas
                                  </Link>
                                  <button className="btn-ghost btn-inline" type="button" onClick={closeDetail}>
                                    Cerrar
                                  </button>
                                </div>
                              </div>

                              <div className="detail-summary-grid">
                                <article className="detail-summary-card">
                                  <span>Cliente</span>
                                  <strong>{rowDetail.clienteNombre || 'General'}</strong>
                                  <small>{rowDetail.clienteId || 'Sin cliente'}</small>
                                </article>
                                <article className="detail-summary-card">
                                  <span>Destino</span>
                                  <strong>{rowDetail.localidadNombre || 'Sin localidad'}</strong>
                                  <small>{rowDetail.localidadId || 'Sin destino'}</small>
                                </article>
                                <article className="detail-summary-card">
                                  <span>Origen</span>
                                  <strong>{rowDetail.localidadOrigenNombre || 'Sin origen'}</strong>
                                  <small>{rowDetail.localidadOrigen || 'Sin filtro de origen'}</small>
                                </article>
                                <article className="detail-summary-card">
                                  <span>Vigencia</span>
                                  <strong>
                                    {formatDate(rowDetail.vigenciaDesde)} - {formatDate(rowDetail.vigenciaHasta)}
                                  </strong>
                                  <small>{rowDetail.activo ? 'Activa' : 'Inactiva'}</small>
                                </article>
                                <article className="detail-summary-card">
                                  <span>Prioridad</span>
                                  <strong>{rowDetail.prioridad}</strong>
                                  <small>{rowDetail.tipoModelo || 'PAQUETE'}</small>
                                </article>
                                <article className="detail-summary-card">
                                  <span>Control volumetrico</span>
                                  <strong>
                                    {rowDetail.divisorVolumetrico || 'Sin divisor'} /{' '}
                                    {rowDetail.topeKgVolumetrico || 'Sin tope'}
                                  </strong>
                                  <small>Orden {rowDetail.orden ?? '-'}</small>
                                </article>
                              </div>

                              {detailLoading && rowIsOpen ? (
                                <p className="empty-state">Cargando detalle real de esta tarifa...</p>
                              ) : null}

                              {detailError && rowIsOpen ? <div className="alert error">{detailError}</div> : null}

                              {!detailLoading && !detailError ? (
                                <div className="detail-rules-shell">
                                  <div className="detail-rules-head">
                                    <div>
                                      <h4>Reglas</h4>
                                      <p className="table-muted">
                                        {rowDetail.reglasActivas || 0} activas / {rowDetail.reglasTotal || 0} total
                                      </p>
                                    </div>
                                    <span className="status-chip status-chip--info">Detalle real</span>
                                  </div>

                                  {detailRules.length > 0 ? (
                                    <div className="detail-rule-list">
                                      {detailRules.map((rule) => (
                                        <article key={rule.id} className="detail-rule-card">
                                          <div className="detail-rule-head">
                                            <div>
                                              <strong>
                                                {rule.orden}. {rule.nombre || `Regla ${rule.id}`}
                                              </strong>
                                              <div className="table-muted">{rule.tipoCalculo || 'FIJO'}</div>
                                            </div>
                                            <span
                                              className={`status-chip ${
                                                rule.activo ? 'status-chip--healthy' : 'status-chip--down'
                                              }`}
                                            >
                                              {rule.activo ? 'Activa' : 'Inactiva'}
                                            </span>
                                          </div>

                                          <div className="table-muted">{buildRuleConditionSummary(rule)}</div>
                                          <div className="table-muted">{buildRulePricingSummary(rule)}</div>
                                        </article>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="empty-state">No hay reglas cargadas para este maestro.</p>
                                  )}
                                </div>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
